import { useClientPresentationDesignerContext } from '../../../ClientPresentationDesigner.context';
import { usePresentationEntityActionsContext } from '../presentationEntityActions.context';
import { PresentationViewSourceCitation } from '../presentationViewSource.utils';

interface UseViewSourceActionResult {
  /**
   * Opens the record's citation. Undefined when the record has nothing to cite
   * OR the action isn't wired up on this surface — so one falsy check covers
   * "don't offer View source here".
   */
  viewSource: (() => void) | undefined;
}

/**
 * Resolves the "View source" affordance for a household record — an entity,
 * individual, organization, or sub-trust — by id.
 *
 * The citation comes from the designer context's `viewSourceMap` unless the
 * caller passes one explicitly, which surfaces that carry the citation on their
 * own fragment do (the entity cover slide, shared with the entity preview modal).
 *
 * Note that `useClientPresentationDesignerContext` has a non-null default, so
 * outside the designer this returns an empty map rather than throwing — the
 * button then simply doesn't render. That's intended, not a missing provider.
 */
export function useViewSourceAction(
  recordId: string | undefined,
  citation?: PresentationViewSourceCitation | null
): UseViewSourceActionResult {
  const { viewSourceMap } = useClientPresentationDesignerContext();
  const actions = usePresentationEntityActionsContext();
  const onViewSource = actions?.onViewSource;

  const resolvedCitation =
    citation ?? (recordId ? viewSourceMap[recordId] : undefined);

  if (!onViewSource || !resolvedCitation) {
    return { viewSource: undefined };
  }

  const { suggestionId, hasSourceDocument } = resolvedCitation;

  if (suggestionId) {
    return {
      viewSource: () => onViewSource({ kind: 'suggestion', suggestionId }),
    };
  }

  // No suggestion, so use the entity's explicit document associations, of any
  // relationship type. `hasSourceDocument` is only ever true for entities,
  // which makes `recordId` an entity id here.
  if (hasSourceDocument && recordId) {
    return {
      viewSource: () =>
        onViewSource({ kind: 'sourceDocuments', entityId: recordId }),
    };
  }

  return { viewSource: undefined };
}
